import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, ShieldAlert, Users, Map, BarChart2, BookOpen, FileText,
  GitCompare, Scale, Settings, Shield, LogOut, Moon, Sun,
  CalendarDays, Crosshair,
} from 'lucide-react'
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup,
  CommandItem, CommandSeparator,
} from '@/components/ui/command'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from 'next-themes'
import { campaignsSurfaceEnabled } from '@/lib/campaigns-surface'

const PAGES = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/vulnerabilities', icon: ShieldAlert, label: 'Vulnerabilities' },
  { to: '/threat-actors', icon: Users, label: 'Threat Actors' },
  { to: '/map', icon: Map, label: 'Live Map' },
  { to: '/analytics', icon: BarChart2, label: 'Analytics' },
  { to: '/benchmarks', icon: BookOpen, label: 'Benchmarks' },
  { to: '/regulatory', icon: FileText, label: 'Regulatory' },
  // KAN-256 — restored alongside Compare; see RootLayout for why the
  // original "no useful data" premise was wrong (range cap, not empty data).
  { to: '/regulatory/calendar', icon: CalendarDays, label: 'Regulatory Calendar' },
  { to: '/regulatory/compare', icon: GitCompare, label: 'Regulatory Compare' },
  { to: '/regulatory-actions', icon: Scale, label: 'Regulatory Actions' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/admin', icon: Shield, label: 'Admin' },
] as const

/**
 * KAN-736 R24 (KAN-830 PR 10) — campaigns are returned as campaigns, never
 * under Threat Actors; the entry shows only with the campaign surface flag.
 */
export function paletteCampaignPages(): ReadonlyArray<{ to: string; icon: typeof Users; label: string }> {
  return campaignsSurfaceEnabled() ? [{ to: '/campaigns', icon: Crosshair, label: 'Campaigns' }] : []
}

const RECENT_KEY = 'intelhub.cmdk.recent'
const MAX_RECENT = 5

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : []
  } catch {
    return []
  }
}

function pushRecent(path: string) {
  try {
    const prev = readRecent().filter(p => p !== path)
    const next = [path, ...prev].slice(0, MAX_RECENT)
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

export interface CommandPaletteProps {
  className?: string
}

export function CommandPalette({ className }: CommandPaletteProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const [recent, setRecent] = useState<string[]>([])

  useEffect(() => { setRecent(readRecent()) }, [open])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(o => !o)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // KAN-209 — lets surfaces without access to this component's state (the
  // dashboard header search affordance) open the palette declaratively.
  useEffect(() => {
    const onOpen = () => setOpen(true)
    document.addEventListener('intelhub:open-command-palette', onOpen)
    return () =>
      document.removeEventListener('intelhub:open-command-palette', onOpen)
  }, [])

  const go = useCallback((path: string) => {
    pushRecent(path)
    setOpen(false)
    navigate(path)
  }, [navigate])

  const pages = [...PAGES.slice(0, 3), ...paletteCampaignPages(), ...PAGES.slice(3)]
  const recentItems = pages.filter(p => recent.includes(p.to))

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search commands and pages..." className={className} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {recentItems.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentItems.map(({ to, icon: Icon, label }) => (
                <CommandItem key={`recent-${to}`} onSelect={() => go(to)}>
                  <Icon className="mr-2 h-4 w-4" />
                  {label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => { setTheme(theme === 'dark' ? 'light' : 'dark'); setOpen(false) }}>
            {theme === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            Toggle theme
          </CommandItem>
          <CommandItem onSelect={() => { logout(); setOpen(false) }}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />

        <CommandGroup heading="Pages">
          {pages.map(({ to, icon: Icon, label }) => (
            <CommandItem key={to} onSelect={() => go(to)}>
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
